import React, { useState, useEffect, useRef } from 'react';
import MPDashboardLayout from '../../components/mp/MPDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { settingsApi, usersApi } from '../../lib/mpApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { toast } from 'sonner';
import { TIMEZONES } from '../../lib/timezone';
import {
  Settings, Moon, Sun, Lock, Save, Loader2, Upload, Globe, Bell, Zap,
  Radio, FileText, Users, Shield,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MPSettingsPage = () => {
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  // V14.10.3 — Observers get a minimal Settings page: Notifications + Security only.
  const isObserverOnly = (user?.roles || []).includes('observer')
    && !(user?.roles || []).some(r => ['super_admin', 'hub_admin', 'simcel'].includes(r));
  const [passwordData, setPasswordData] = useState({ new_password: '', confirm: '' });
  const [logoUrl, setLogoUrl] = useState('');
  const logoInputRef = useRef(null);

  const [settings, setSettings] = useState({
    default_timezone: 'UTC',
    auto_publish: false,
    auto_scroll_speed: 5,
    portal_name: 'Media Portal',
    portal_tagline: 'Newsroom Command Center',
    notification_new_post: true,
    notification_exercise_status: true,
    notification_user_activity: false,
    default_post_status: 'draft',
    enable_guest_comments: true,
    max_upload_size_mb: 50,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await settingsApi.getAll();
        if (data) {
          setSettings(prev => ({ ...prev, ...data }));
          if (data.logo_url) setLogoUrl(data.logo_url);
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await settingsApi.update(settings);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async () => {
    if (!passwordData.new_password) { toast.error('Enter new password'); return; }
    if (passwordData.new_password !== passwordData.confirm) { toast.error('Passwords do not match'); return; }
    if (passwordData.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSavingPassword(true);
    try {
      await usersApi.updatePassword(user.user_id, passwordData.new_password);
      toast.success('Password updated');
      setPasswordData({ new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update password'); }
    finally { setSavingPassword(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/mp/settings/logo`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setLogoUrl(data.logo_url || '');
        toast.success('Logo updated');
      }
    } catch { toast.error('Upload failed'); }
  };

  const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  if (loading) return <MPDashboardLayout title="Settings"><div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div></MPDashboardLayout>;

  return (
    <MPDashboardLayout title="Settings">
      <div className="space-y-6 max-w-3xl" data-testid="mp-settings-page">

        {/* Portal Branding — V14.10.3: hidden for observers */}
        {!isObserverOnly && (
        <>
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2 text-foreground/80">
              <Radio className="w-4 h-4 text-red-400" /> Portal Branding
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg bg-muted border border-border flex items-center justify-center overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl.startsWith('/') ? `${BACKEND_URL}${logoUrl}` : logoUrl} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <Radio className="w-6 h-6 text-muted-foreground" />
                )}
              </div>
              <div>
                <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                <Button variant="outline" onClick={() => logoInputRef.current?.click()}
                  className="border-border text-foreground/80 hover:bg-muted" data-testid="mp-settings-upload-logo">
                  <Upload className="w-4 h-4 mr-2" /> Upload Logo
                </Button>
                <p className="text-xs text-muted-foreground mt-1">Recommended: 200x200px PNG</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Portal Name</Label>
                <Input value={settings.portal_name} onChange={e => updateSetting('portal_name', e.target.value)}
                  className="bg-muted border-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tagline</Label>
                <Input value={settings.portal_tagline} onChange={e => updateSetting('portal_tagline', e.target.value)}
                  className="bg-muted border-input" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2 text-foreground/80">
              {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />} Appearance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Dark Mode</p>
                <p className="text-xs text-muted-foreground">Toggle between dark and light theme</p>
              </div>
              <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="mp-settings-theme-toggle" />
            </div>
          </CardContent>
        </Card>

        {/* General */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2 text-foreground/80">
              <Globe className="w-4 h-4" /> General
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Default Timezone</Label>
              <Select value={settings.default_timezone} onValueChange={v => updateSetting('default_timezone', v)}>
                <SelectTrigger className="bg-muted border-input"><SelectValue /></SelectTrigger>
                <SelectContent>{TIMEZONES.map(tz => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Default Post Status</Label>
              <Select value={settings.default_post_status} onValueChange={v => updateSetting('default_post_status', v)}>
                <SelectTrigger className="bg-muted border-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Max Upload Size (MB)</Label>
              <div className="flex items-center gap-3">
                <Slider value={[settings.max_upload_size_mb]} onValueChange={([v]) => updateSetting('max_upload_size_mb', v)}
                  min={10} max={200} step={10} className="flex-1" />
                <span className="text-sm text-muted-foreground font-mono w-14 text-right">{settings.max_upload_size_mb} MB</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Live Display */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2 text-foreground/80">
              <Zap className="w-4 h-4 text-amber-400" /> Live Display
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Auto-Publish Posts</p>
                <p className="text-xs text-muted-foreground">Automatically publish new posts to the live display</p>
              </div>
              <Switch checked={settings.auto_publish} onCheckedChange={v => updateSetting('auto_publish', v)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Auto-Scroll Speed</Label>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">Slow</span>
                <Slider value={[settings.auto_scroll_speed]} onValueChange={([v]) => updateSetting('auto_scroll_speed', v)}
                  min={1} max={10} step={1} className="flex-1" />
                <span className="text-xs text-muted-foreground">Fast</span>
                <span className="text-sm text-muted-foreground font-mono w-8 text-right">{settings.auto_scroll_speed}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Guest Comments</p>
                <p className="text-xs text-muted-foreground">Allow guests to post comments on the live display</p>
              </div>
              <Switch checked={settings.enable_guest_comments} onCheckedChange={v => updateSetting('enable_guest_comments', v)} />
            </div>
          </CardContent>
        </Card>
        </>
        )}

        {/* Notifications — hidden for observers */}
        {!isObserverOnly && (<>
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2 text-foreground/80">
              <Bell className="w-4 h-4" /> Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { key: 'notification_new_post', label: 'New Post Created', desc: 'Notify when a new post is created' },
              { key: 'notification_exercise_status', label: 'Exercise Status Change', desc: 'Notify when exercise status changes' },
              { key: 'notification_user_activity', label: 'User Activity', desc: 'Notify on user login and actions' },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
                <Switch checked={settings[item.key]} onCheckedChange={v => updateSetting(item.key, v)} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Save All Settings */}
        <div className="flex justify-end">
          <Button onClick={handleSaveSettings} disabled={saving} className="bg-red-600 hover:bg-red-700 text-foreground"
            data-testid="mp-settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save All Settings
          </Button>
        </div>
        </>)}

        {/* Security */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2 text-foreground/80">
              <Lock className="w-4 h-4" /> Security
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">New Password</Label>
              <Input type="password" value={passwordData.new_password}
                onChange={e => setPasswordData({...passwordData, new_password: e.target.value})}
                placeholder="New password"
                className="bg-muted border-input"
                data-testid="mp-settings-new-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm Password</Label>
              <Input type="password" value={passwordData.confirm}
                onChange={e => setPasswordData({...passwordData, confirm: e.target.value})}
                placeholder="Confirm password"
                className="bg-muted border-input"
                data-testid="mp-settings-confirm-password" />
            </div>
            <Button onClick={handlePasswordChange} disabled={savingPassword}
              variant="outline" className="border-border text-foreground/80 hover:bg-muted"
              data-testid="mp-settings-change-password-btn">
              {savingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
              Update Password
            </Button>
          </CardContent>
        </Card>
      </div>
    </MPDashboardLayout>
  );
};

export default MPSettingsPage;
